
import { Component, Prop, Vue } from 'vue-property-decorator';
import ResultItem from './results/result-item.vue';
import { container } from '../lib/container';
import { Actions, ActionStates } from '../lib/Actions';
import { ContainerTypes } from '../lib/core/types/ContainerTypes';
import { isResultEntry, isSearchEntries } from '../lib/core/guards';
import { ResultEntry } from '../lib/interfaces/search/ResultEntry';
import { gteDevice, allowDevices, denyDevices } from '../lib/core/responsive';
import { I18n } from '../lib/interfaces/I18n';
import deDE from '../lib/presets/resources/de-DE';
import { SearchStorage } from '../lib/core/storage/SearchStorage';
import { execCmd, setCmd } from '../lib/core/storage/CommandStorage';
import SimpleBar from './simplebar.vue';
import { getConfig } from '../lib/ConfigLoader';
import WelcomeSiedeView from './welcomeParts/welcome-side.vue';
import { AppBehavior } from '../lib/core/AppBehavior';
import { MutationTypes } from '../store/mutation-types';

const behavior = container.get<AppBehavior>('behavior');

const actions = container.get<Actions>(ContainerTypes.Action);
const storage = container.get<SearchStorage>(ContainerTypes.SearchStorage);

actions.on(ActionStates.Initialize, (from, event) => {
	execCmd('onInitialize', from, event);
});

actions.on(ActionStates.HomePage, () => {
	execCmd('ResultsView.onHomePage');
});

actions.on(ActionStates.CallRouting, (from, event) => {
	execCmd('resultsCallRouting', from, event);
});

actions.on(ActionStates.CallDetails, (from, event) => {
	execCmd('resultsCallDetails', from, event);
});

actions.on(ActionStates.CallResults, (from, event) => {
	execCmd('callResults', from, event);
});

actions.on(ActionStates.UpdateResults, (from, event) => {
	execCmd('updateResults', from, event);
});

type SideContainer = { offsetTop: number; offsetLeft: number; clientWidth: number };

@Component({ components: { ResultItem, SimpleBar, WelcomeSiedeView } })
export default class ResultsView extends Vue {
	@Prop() provider!: any;

	$refs!: {
		sideContainer: HTMLElement;
		resultItems: ResultItem[];
		resultHeader: HTMLElement;
	};

	i18n: I18n = deDE;

	isActive = false;
	isNotRouting = true;
	hasError = false;
	entries: ResultEntry[] = [];

	// returns true if the container is closed (sidebar is collapsed)
	get isContainerClosed(): boolean {
		const { isMapVisible } = this.$store.state;

		return isMapVisible;
	}

	get hideMapToggle(): boolean {
		return this.isHomePageAndApp || this.$store.getters.displayFullPoiDetail;
	}

	get onHome(): boolean {
		return this.$store.getters.onHome;
	}
	get isHomePageAndApp(): boolean {
		const { onHome } = this.$store.getters;
		const { appView } = behavior;
		return appView && onHome;
	}

	toggleMap() {
		this.$store.commit(MutationTypes.toggleMap);
	}

	onResize() {
		if (gteDevice('mobile')) {
			this.$store.commit(MutationTypes.setMapVisible, false);
		}
	}

	mounted() {
		// this.isHomePageAndApp = this.updateIsHomePageAndApp();
		const config = getConfig();
		setCmd('resultsIdle', (from, event) => {
			this.isNotRouting = true;
		});

		setCmd('ResultsView.onHomePage', () => {
			this.entries = [];
			this.isActive = false;
			this.isNotRouting = true;
			this.$store.commit(MutationTypes.setMapVisible, false);
			// this.isHomePageAndApp = this.updateIsHomePageAndApp();
		});

		setCmd('resultsCallDetails', (from, event) => {
			this.isActive = denyDevices(config.resultsView.onDetails.hidesAt);
			if (event.from === 'list') {
				this.$store.commit(MutationTypes.setMapVisible, true);
			}
			this.isNotRouting = true;
		});
		setCmd('resultsCallRouting', (from, event) => {
			this.isActive = denyDevices(config.resultsView.onRouting.hidesAt);
			this.isNotRouting =
				!config.resultsView.scrolling.autoScroll || !allowDevices(config.resultsView.scrolling.sizes);
		});
		setCmd('callResults', (from, event) => {
			this.isNotRouting = true;

			actions.go(ActionStates.UpdateResults, event);
		});
		setCmd('updateResults', (from, event) => {
			// this.isHomePageAndApp = this.updateIsHomePageAndApp();
			this.isNotRouting = true;

			if (isSearchEntries(storage.results)) {
				this.hasError = false;
				if (!storage.results.error && !storage.results.entries.length) {
					storage.results.error = 'No results found';
				}

				if (storage.results.error) {
					this.hasError = true;
					this.entries = [];
					this.$nextTick(() => actions.go(ActionStates.Error, storage.results.error));
				} else {
					this.entries = storage.results.entries.filter(entry => isResultEntry(entry)) as ResultEntry[];
					this.$store.commit(MutationTypes.setMapVisible, this.entries.length >= 0);
					const result = {
						...event,
						type: 'updateResults',
						position: storage.searchCenter
					};

					this.isActive = true;

					// execute after sideContainer is rendered!
					this.$nextTick(() => {
						if (this.$refs.resultHeader) {
							this.$refs.resultHeader.scrollIntoView({
								behavior: 'smooth',
								block: 'start'
							});
						}

						const { offsetTop, offsetLeft, clientWidth } = this.getSideContainer();
						this.$refs.sideContainer;
						result.offset = {
							top: offsetTop,
							right: 0,
							bottom: 0,
							left: offsetLeft + clientWidth
						};
						actions.go(ActionStates.UpdateMap, result);
					});
				}
			}
		});

		window.addEventListener('resize', this.onResize);
		this.onResize();
	}

	private getSideContainer(): SideContainer {
		let element: HTMLElement | null = this.$refs.sideContainer;
		if (element == null) {
			element = document.getElementById('sideContainer');
		}

		const nullResult: SideContainer = { offsetTop: 0, offsetLeft: 0, clientWidth: 0 };
		return element || nullResult;
	}

	beforeDestroy() {
		window.removeEventListener('resize', this.onResize);
	}
}
